import { useState, useEffect } from "react";
import { Car, Calendar, Gauge, ChevronDown, Plus, Trash2 } from "lucide-react";

// Vehicle brands with their models (popular in Turkey)
const VEHICLE_DATA: Record<string, string[]> = {
  "Audi": ["A1", "A3", "A4", "A5", "A6", "A7", "A8", "Q2", "Q3", "Q5", "Q7", "Q8", "TT", "e-tron"],
  "BMW": ["1 Series", "2 Series", "3 Series", "4 Series", "5 Series", "6 Series", "7 Series", "8 Series", "X1", "X2", "X3", "X4", "X5", "X6", "X7", "Z4", "i3", "i4", "iX"],
  "Citroen": ["C1", "C3", "C3 Aircross", "C4", "C4 Cactus", "C5", "C5 Aircross", "Berlingo", "SpaceTourer"],
  "Dacia": ["Sandero", "Duster", "Logan", "Jogger", "Spring"],
  "Fiat": ["500", "500X", "500L", "Panda", "Tipo", "Egea", "Doblo", "Fiorino", "Linea", "Punto"],
  "Ford": ["Fiesta", "Focus", "Mondeo", "Kuga", "EcoSport", "Puma", "Tourneo", "Transit", "Ranger", "Mustang"],
  "Honda": ["Civic", "Jazz", "HR-V", "CR-V", "Accord", "City"],
  "Hyundai": ["i10", "i20", "i30", "Tucson", "Kona", "Santa Fe", "Elantra", "Bayon", "IONIQ"],
  "Kia": ["Picanto", "Rio", "Ceed", "Sportage", "Sorento", "Stonic", "Niro", "EV6", "Seltos"],
  "Mercedes-Benz": ["A-Class", "B-Class", "C-Class", "E-Class", "S-Class", "CLA", "CLS", "GLA", "GLB", "GLC", "GLE", "GLS", "G-Class", "EQA", "EQB", "EQC", "EQE", "EQS"],
  "Nissan": ["Micra", "Juke", "Qashqai", "X-Trail", "Leaf", "Note", "Navara"],
  "Opel": ["Corsa", "Astra", "Insignia", "Mokka", "Crossland", "Grandland", "Combo", "Vivaro"],
  "Peugeot": ["108", "208", "308", "408", "508", "2008", "3008", "5008", "Rifter", "Partner"],
  "Renault": ["Clio", "Megane", "Kadjar", "Captur", "Talisman", "Koleos", "Kangoo", "Zoe", "Twingo"],
  "Seat": ["Ibiza", "Leon", "Arona", "Ateca", "Tarraco", "Mii"],
  "Skoda": ["Fabia", "Scala", "Octavia", "Superb", "Kamiq", "Karoq", "Kodiaq", "Enyaq"],
  "Toyota": ["Yaris", "Corolla", "Camry", "RAV4", "C-HR", "Land Cruiser", "Hilux", "Proace", "Auris", "Avensis"],
  "Volkswagen": ["Polo", "Golf", "Passat", "Arteon", "Tiguan", "T-Roc", "T-Cross", "Touareg", "Caddy", "Transporter", "ID.3", "ID.4"],
  "Volvo": ["XC40", "XC60", "XC90", "S60", "S90", "V40", "V60", "V90"],
  "Other": ["Other Model"],
};

const BRANDS = Object.keys(VEHICLE_DATA).sort();

// Generate years from current year to 1990
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1989 }, (_, i) => CURRENT_YEAR - i);

interface VehicleEntry {
  id: string;
  licensePlate: string;
  brand: string;
  model: string;
  year: string;
  mileage: string;
}

interface VehicleInfoFormProps {
  value: string;
  onChange: (jsonValue: string) => void;
}

const createEmptyVehicle = (): VehicleEntry => ({
  id: `vehicle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  licensePlate: "",
  brand: "",
  model: "",
  year: "",
  mileage: "",
});

export function VehicleInfoForm({ value, onChange }: VehicleInfoFormProps) {
  const [vehicles, setVehicles] = useState<VehicleEntry[]>([createEmptyVehicle()]);

  // Parse existing JSON value on mount
  useEffect(() => {
    if (value) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const mapped = parsed.map((v: any, index: number) => ({
            id: `vehicle_${index}_${Date.now()}`,
            licensePlate: v.licensePlate || "",
            brand: v.make || v.brand || "",
            model: v.model || "",
            year: v.year?.toString() || "",
            mileage: v.mileage?.toString() || v.km?.toString() || "",
          }));
          setVehicles(mapped);
        }
      } catch (e) {
        // Invalid JSON, keep default
      }
    }
  }, []);

  // Update parent when vehicles change
  useEffect(() => {
    const validVehicles = vehicles.filter(
      (v) => v.brand || v.model || v.licensePlate || v.year || v.mileage
    );
    
    if (validVehicles.length === 0) {
      onChange("");
      return;
    }

    const jsonOutput = validVehicles.map((v) => ({
      licensePlate: v.licensePlate || undefined,
      make: v.brand || undefined,
      model: v.model || undefined,
      year: v.year ? parseInt(v.year) : undefined,
      mileage: v.mileage ? parseInt(v.mileage.replace(/\D/g, "")) : undefined,
    }));

    onChange(JSON.stringify(jsonOutput, null, 2));
  }, [vehicles, onChange]);

  const updateVehicle = (id: string, field: keyof VehicleEntry, value: string) => {
    setVehicles((prev) =>
      prev.map((v) => {
        if (v.id !== id) return v;
        
        // If brand changes, reset model
        if (field === "brand") {
          return { ...v, brand: value, model: "" };
        }
        
        return { ...v, [field]: value };
      })
    );
  };

  const addVehicle = () => {
    setVehicles((prev) => [...prev, createEmptyVehicle()]);
  };

  const removeVehicle = (id: string) => {
    if (vehicles.length === 1) {
      // Reset the only vehicle instead of removing
      setVehicles([createEmptyVehicle()]);
    } else {
      setVehicles((prev) => prev.filter((v) => v.id !== id));
    }
  };

  const formatMileage = (value: string) => {
    const numbers = value.replace(/\D/g, "");
    return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  return (
    <div className="space-y-4">
      {vehicles.map((vehicle, index) => (
        <div
          key={vehicle.id}
          className="bg-gray-50 dark:bg-slate-800 rounded-xl p-4 border border-gray-200 dark:border-slate-700 space-y-4 transition-colors"
        >
          {/* Vehicle Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Car className="w-5 h-5 text-slate-600 dark:text-slate-400" />
              <span className="font-medium text-gray-900 dark:text-white">
                Vehicle {index + 1}
              </span>
            </div>
            <button
              type="button"
              onClick={() => removeVehicle(vehicle.id)}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
              title="Remove vehicle"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* License Plate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
              License Plate
            </label>
            <input
              type="text"
              value={vehicle.licensePlate}
              onChange={(e) =>
                updateVehicle(vehicle.id, "licensePlate", e.target.value.toUpperCase())
              }
              placeholder="34 ABC 123"
              className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500"
            />
          </div>

          {/* Brand and Model Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Brand Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Brand
              </label>
              <div className="relative">
                <select
                  value={vehicle.brand}
                  onChange={(e) => updateVehicle(vehicle.id, "brand", e.target.value)}
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer text-gray-900 dark:text-white"
                >
                  <option value="" className="dark:bg-slate-700">Select brand</option>
                  {BRANDS.map((brand) => (
                    <option key={brand} value={brand} className="dark:bg-slate-700">
                      {brand}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Model Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Model
              </label>
              <div className="relative">
                <select
                  value={vehicle.model}
                  onChange={(e) => updateVehicle(vehicle.id, "model", e.target.value)}
                  disabled={!vehicle.brand}
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer disabled:bg-gray-100 dark:disabled:bg-slate-800 disabled:cursor-not-allowed text-gray-900 dark:text-white"
                >
                  <option value="" className="dark:bg-slate-700">Select model</option>
                  {vehicle.brand &&
                    VEHICLE_DATA[vehicle.brand]?.map((model) => (
                      <option key={model} value={model} className="dark:bg-slate-700">
                        {model}
                      </option>
                    ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>

          {/* Year and Mileage Row */}
          <div className="grid grid-cols-2 gap-3">
            {/* Year Picker */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                  Year
                </div>
              </label>
              <div className="relative">
                <select
                  value={vehicle.year}
                  onChange={(e) => updateVehicle(vehicle.id, "year", e.target.value)}
                  className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm appearance-none cursor-pointer text-gray-900 dark:text-white"
                >
                  <option value="" className="dark:bg-slate-700">Select year</option>
                  {YEARS.map((year) => (
                    <option key={year} value={year} className="dark:bg-slate-700">
                      {year}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Mileage Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Gauge className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                  Mileage (KM)
                </div>
              </label>
              <input
                type="text"
                value={formatMileage(vehicle.mileage)}
                onChange={(e) =>
                  updateVehicle(vehicle.id, "mileage", e.target.value.replace(/\D/g, ""))
                }
                placeholder="50,000"
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500"
              />
            </div>
          </div>
        </div>
      ))}

      {/* Add Vehicle Button */}
      <button
        type="button"
        onClick={addVehicle}
        className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-gray-600 dark:text-slate-400 hover:border-blue-400 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all"
      >
        <Plus className="w-4 h-4" />
        Add Another Vehicle
      </button>
    </div>
  );
}






